import { InstrumentBar, PlayButton, Telemetry, type Readout } from "./shell";
import { useReducedMotion, useStepper, useSweep, type NonEmpty } from "./useInstrument";

/* =====================================================================
   The record — one sample review on a real time axis.

   Deliberately a DIFFERENT graphic from the pipeline, so the two do not
   read as the same figure twice: the pipeline shows a distribution, this
   shows elapsed time. Node positions are real minutes across the hour,
   which is what makes the 27-minute wait on the prescriber measurable
   rather than implied — that gap is the thing the product takes out of
   somebody's inbox, so it earns being drawn.

   Two seats write and two read. The write/read split is structural in the
   product (portal identities are a separate system from staff accounts),
   so the figure must not blur it.
   ===================================================================== */

const TOTAL_MINUTES = 60;

/** Position a minute offset along the rail, with a little padding at each end. */
function atPct(minute: number): number {
  return 4 + (minute / TOTAL_MINUTES) * 92;
}

type SeatKey = "rx" | "md" | "adm" | "don";
type SeatTone = "" | "live" | "wait";

interface Entry {
  time: string;
  minute: number;
  actor: SeatKey;
  who: string;
  title: string;
  detail: string;
  cites: readonly string[];
  state: string;
  seats: Record<SeatKey, [SeatTone, string]>;
}

const SEATS: readonly { key: SeatKey; name: string; role: string; writes: boolean }[] = [
  { key: "rx", name: "Pharmacist", role: "Writes", writes: true },
  { key: "md", name: "Prescriber", role: "Writes", writes: true },
  { key: "adm", name: "Facility admin", role: "Reads", writes: false },
  { key: "don", name: "DON", role: "Reads", writes: false },
];

const ENTRIES: NonEmpty<Entry> = [
  {
    time: "09:12", minute: 0, actor: "rx", who: "Pharmacist",
    title: "Review opened",
    detail: "Chart ingested and normalized. The clinical engine raises 18 findings, each already tied to the rule it came from.",
    cites: [], state: "Open",
    seats: { rx: ["live", "Reviewing"], md: ["", "Nothing sent"], adm: ["", "18 open"], don: ["", "Review in progress"] },
  },
  {
    time: "09:29", minute: 17, actor: "rx", who: "Pharmacist",
    title: "Findings triaged",
    detail: "Three critical, six moderate, nine routine. Severity is the pharmacist’s call; the citation behind it is not editable.",
    cites: ["Beers 2023", "STOPP/START v2", "Boustani ACB"], state: "Open",
    seats: { rx: ["live", "Triaged"], md: ["", "Nothing sent"], adm: ["", "18 open · 3 critical"], don: ["wait", "3 critical flagged"] },
  },
  {
    time: "09:41", minute: 29, actor: "rx", who: "Pharmacist",
    title: "Recommendation signed",
    detail: "Taper recommended for anticholinergic burden ≥ 3 with concurrent fall risk. It reaches the prescriber inside the record, not in an inbox.",
    cites: ["Beers 2023 · Table 8", "F605 · §483.45(e)"], state: "Waiting",
    seats: { rx: ["live", "1 signed"], md: ["wait", "Awaiting response"], adm: ["wait", "17 open · 1 waiting"], don: ["wait", "1 awaiting prescriber"] },
  },
  {
    time: "10:08", minute: 56, actor: "md", who: "Prescriber",
    title: "Response recorded — accepted",
    detail: "Accepted with no edits. The answer attaches to the 09:41 entry, so the recommendation and its outcome stay one thing, not two.",
    cites: ["Responds to 09:41"], state: "Answered",
    seats: { rx: ["live", "Ready to close"], md: ["live", "Responded · accepted"], adm: ["", "17 open · 0 waiting"], don: ["", "0 awaiting"] },
  },
  {
    time: "10:12", minute: 60, actor: "rx", who: "Pharmacist",
    title: "Bundle closed & invoiced",
    detail: "Signature, audit entry, and invoice from the same review. The record seals — appended to, never rewritten.",
    cites: ["Entry locked · 7-year retention"], state: "Sealed",
    seats: { rx: ["live", "Signed & closed"], md: ["live", "Responded · accepted"], adm: ["live", "Bundle closed"], don: ["live", "Standing: current"] },
  },
];

/** Quarter-hour labels, positioned by real elapsed minutes. */
const AXIS_LABELS: readonly [number, string][] = [
  [3, "09:15"],
  [18, "09:30"],
  [33, "09:45"],
  [48, "10:00"],
];

const MINOR_TICKS = Array.from({ length: TOTAL_MINUTES / 5 + 1 }, (_, i) => i * 5);

// The prescriber wait, drawn to the same scale as everything else.
const WAIT_FROM = 29;
const WAIT_TO = 56;

export function RecordLedger({ dark = false }: { dark?: boolean }) {
  const reduced = useReducedMotion();
  const { at, current: entry, playing, containerRef, go, toggle, onKeyDown } = useStepper({
    items: ENTRIES,
    intervalMs: 1600,
    reduced,
  });
  const sweepRef = useSweep(at, reduced);
  const last = at === ENTRIES.length - 1;

  const tele: readonly Readout[] = [
    { value: `${at + 1} / 5`, label: "Entries" },
    { value: String(at + 1), label: "Writes" },
    { value: "4", label: "Seats in sync" },
    { value: `${entry.minute} min`, label: "Elapsed" },
    { value: entry.state, label: "Record state" },
  ];

  return (
    <div ref={containerRef} className={`inst is-compact${dark ? " is-dark" : ""}`}>
      <div className="inst-device">
        <InstrumentBar title="The record · one sample review, four seats">
          <span className="inst-clock">{entry.time}</span>
          <PlayButton playing={playing} atEnd={last} onClick={toggle} label="Play the review from this point" />
        </InstrumentBar>

        <div className="inst-plot">
          <span ref={sweepRef} className="inst-sweep" aria-hidden="true" />

          <Telemetry readouts={tele} hot={4} reduced={reduced} />

          <div className="lg-seats">
            {SEATS.map((seat) => {
              const [tone, text] = entry.seats[seat.key];
              return (
                <div
                  key={seat.key}
                  className={`lg-seat${seat.writes ? " is-writer" : ""}${
                    seat.key === entry.actor ? " is-actor" : ""
                  }`}
                >
                  <div className="lg-seat-top">
                    <p className="lg-name">{seat.name}</p>
                    <span className="lg-role-tag">{seat.role}</span>
                  </div>
                  <span className={`lg-state${tone ? ` st-${tone}` : ""}`}>{text}</span>
                </div>
              );
            })}
          </div>

          <div className="lg-rail-wrap">
            <div className="lg-rail" role="tablist" aria-label="Record entries" onKeyDown={onKeyDown}>
              <span className="lg-line" aria-hidden="true" />
              <span
                className="lg-wait"
                style={{ left: `${atPct(WAIT_FROM)}%`, width: `${atPct(WAIT_TO) - atPct(WAIT_FROM)}%` }}
                aria-hidden="true"
              />
              <span
                className="lg-wait-l"
                style={{ left: `${(atPct(WAIT_FROM) + atPct(WAIT_TO)) / 2}%` }}
                aria-hidden="true"
              >
                27 min waiting on prescriber
              </span>
              <span className="lg-fill" style={{ width: `${atPct(entry.minute)}%` }} aria-hidden="true" />
              <span className="lg-cap lg-cap-l" aria-hidden="true" />
              <span className={`lg-cap lg-cap-r${last ? " is-sealed" : ""}`} aria-hidden="true" />

              {ENTRIES.map((e, i) => (
                <button
                  key={e.time}
                  type="button"
                  role="tab"
                  aria-selected={i === at}
                  tabIndex={i === at ? 0 : -1}
                  id={`ledger-tab-${i}`}
                  aria-controls={`ledger-panel-${i}`}
                  aria-label={`${e.time} — ${e.title}`}
                  className={`lg-node${i < at ? " is-done" : ""}${i === at ? " is-current" : ""}`}
                  style={{ left: `${atPct(e.minute)}%` }}
                  onClick={() => go(i)}
                >
                  <span className="lg-dot" />
                  <span className="lg-t-lab">{e.time}</span>
                </button>
              ))}
            </div>

            <div className="lg-axis" aria-hidden="true">
              {MINOR_TICKS.map((m) => (
                <span key={`t${m}`} className="lg-tick" style={{ left: `${atPct(m)}%` }} />
              ))}
              {AXIS_LABELS.map(([m, label]) => (
                <span key={label}>
                  <span className="lg-tick is-major" style={{ left: `${atPct(m)}%` }} />
                  <span className="lg-ax-l" style={{ left: `${atPct(m)}%` }}>{label}</span>
                </span>
              ))}
            </div>

            <div className="lg-ends">
              <span>Opened 09:12</span>
              <span>{last ? "Sealed 10:12" : "Locks 10:12"}</span>
            </div>
          </div>

          {/* Same reason as the pipeline panels: every entry stays in the
              prerendered HTML, and hiding rather than unmounting is the ARIA
              tabs pattern. */}
          {ENTRIES.map((e, i) => (
            <div
              key={e.time}
              id={`ledger-panel-${i}`}
              className="lg-entry"
              role="tabpanel"
              aria-labelledby={`ledger-tab-${i}`}
              hidden={i !== at}
            >
              <div className={i === at ? "inst-fade" : undefined} key={at}>
                <div className="lg-entry-top">
                  <span className="lg-entry-time">{e.time}</span>
                  <span className="lg-entry-actor">{e.who}</span>
                  <span className="lg-entry-idx">Entry {i + 1} / {ENTRIES.length}</span>
                </div>
                <p className="lg-entry-title">{e.title}</p>
                <p className="lg-entry-detail">{e.detail}</p>
                {e.cites.length > 0 ? (
                  <div className="inst-chips" style={{ marginTop: "0.6rem" }}>
                    {e.cites.map((c) => (
                      <span key={c} className="inst-chip">{c}</span>
                    ))}
                  </div>
                ) : null}
                <p className="lg-visible">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  On all four seats the moment it lands. Nobody re-keys it anywhere.
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="inst-foot">
          <span>Append-only · every action timestamped</span>
          <span className="ok">Nothing re-keyed</span>
        </div>
      </div>
    </div>
  );
}
